import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle2,
  Clock,
  ClipboardList,
  ChevronRight,
  Calendar,
  User,
  RefreshCw,
  Check,
  X,
  Loader2,
  FileText,
  ExternalLink,
  MessageSquare,
  Send,
  ChevronDown
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const statusConfig = {
  pending: { 
    label: 'Pending', 
    color: 'amber', 
    icon: Clock,
    bg: 'bg-amber-100',
    text: 'text-amber-700',
    border: 'border-amber-200'
  },
  in_progress: { 
    label: 'In Progress', 
    color: 'blue', 
    icon: Loader2,
    bg: 'bg-blue-100',
    text: 'text-blue-700',
    border: 'border-blue-200'
  },
  completed: { 
    label: 'Completed', 
    color: 'green', 
    icon: CheckCircle2,
    bg: 'bg-green-100',
    text: 'text-green-700',
    border: 'border-green-200'
  }
};

export default function ClientTasksView({ client, setActiveTab }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedTask, setSelectedTask] = useState(null);
  const [completing, setCompleting] = useState(null);
  const [counts, setCounts] = useState({ pending: 0, in_progress: 0, completed: 0, total: 0 });

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/client/tasks`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setTasks(data.tasks || []);
        setCounts(data.counts || { pending: 0, in_progress: 0, completed: 0, total: 0 });
      }
    } catch (err) {
      console.error('Error fetching tasks:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async (taskId) => {
    setCompleting(taskId);
    try {
      const res = await fetch(`${API}/api/client/tasks/${taskId}/complete`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        fetchTasks();
      }
    } catch (err) {
      console.error('Error completing task:', err);
    } finally {
      setCompleting(null);
    }
  };

  const filteredTasks = tasks.filter(t => {
    if (filter === 'pending') return t.status === 'pending';
    if (filter === 'in_progress') return t.status === 'in_progress';
    if (filter === 'completed') return t.status === 'completed';
    return true;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return 'No due date';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const isOverdue = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date() && dateStr;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-tasks-view">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">My Tasks</h2>
          <p className="text-slate-500">Tasks and action items from your team</p>
        </div>
        {counts.pending > 0 && (
          <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-xl">
            <Clock className="w-5 h-5 text-amber-600" />
            <span className="font-medium text-amber-700">{counts.pending} pending</span>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-2xl font-bold text-slate-800">{counts.total}</p>
          <p className="text-xs text-slate-500">Total Tasks</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-2xl font-bold text-amber-600">{counts.pending}</p>
          <p className="text-xs text-amber-600">Pending</p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="text-2xl font-bold text-blue-600">{counts.in_progress}</p>
          <p className="text-xs text-blue-600">In Progress</p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <p className="text-2xl font-bold text-green-600">{counts.completed}</p>
          <p className="text-xs text-green-600">Completed</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {[
          { id: 'all', label: 'All' },
          { id: 'pending', label: 'Pending' },
          { id: 'in_progress', label: 'In Progress' },
          { id: 'completed', label: 'Completed' }
        ].map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              filter === f.id
                ? 'bg-blue-600 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Tasks List */}
      {filteredTasks.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
          <ClipboardList className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">
            {filter === 'pending' ? 'No pending tasks' : 
             filter === 'completed' ? 'No completed tasks yet' :
             'No tasks yet'}
          </h3>
          <p className="text-slate-500">
            {filter === 'pending' 
              ? 'Great job! You\'re all caught up.' 
              : 'Tasks from your team will appear here'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredTasks.map(task => {
            const status = statusConfig[task.status] || statusConfig.pending;
            const StatusIcon = status.icon;
            const overdue = isOverdue(task.due_date) && task.status !== 'completed';
            const isApprovalTask = task.task_type === 'approval_review';

            return (
              <motion.div
                key={task.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`bg-white rounded-2xl border-2 p-5 hover:shadow-lg transition-all cursor-pointer ${
                  overdue ? 'border-red-200' :
                  task.status === 'pending' ? 'border-amber-200' :
                  task.status === 'in_progress' ? 'border-blue-200' :
                  'border-slate-200'
                }`}
                onClick={() => setSelectedTask(task)}
                data-testid={`task-item-${task.id}`}
              >
                <div className="flex items-start gap-4">
                  {/* Status Icon */}
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${status.bg}`}>
                    <StatusIcon className={`w-6 h-6 ${status.text} ${task.status === 'in_progress' ? 'animate-spin' : ''}`} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-800">{task.title}</h3>
                          {isApprovalTask && (
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                              Approval
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 mt-0.5 line-clamp-2">{task.description}</p>
                      </div>
                      <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium flex-shrink-0 ${status.bg} ${status.text}`}>
                        <StatusIcon className={`w-3.5 h-3.5 ${task.status === 'in_progress' ? 'animate-spin' : ''}`} />
                        {status.label}
                      </span>
                    </div>

                    {/* Meta */}
                    <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-slate-500">
                      {task.due_date && (
                        <span className={`flex items-center gap-1 ${overdue ? 'text-red-600 font-medium' : ''}`}>
                          <Calendar className="w-3.5 h-3.5" />
                          {overdue ? 'Overdue: ' : 'Due: '}{formatDate(task.due_date)}
                        </span>
                      )}
                      {task.sent_by && (
                        <span className="flex items-center gap-1">
                          <User className="w-3.5 h-3.5" />
                          From: {task.sent_by}
                        </span>
                      )}
                      {task.comment_count > 0 && (
                        <span className="flex items-center gap-1 text-blue-600">
                          <MessageSquare className="w-3.5 h-3.5" />
                          {task.comment_count} comment{task.comment_count !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {isApprovalTask && task.linked_approval_id && task.status !== 'completed' && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setActiveTab && setActiveTab('approvals'); }}
                        className="px-3 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 text-sm font-medium rounded-xl flex items-center gap-1.5 transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Review
                      </button>
                    )}
                    {task.status === 'pending' && !isApprovalTask && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleComplete(task.id); }}
                        disabled={completing === task.id}
                        className="px-3 py-2 bg-green-100 hover:bg-green-200 text-green-700 text-sm font-medium rounded-xl flex items-center gap-1.5 transition-colors disabled:opacity-50"
                      >
                        {completing === task.id ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4" />
                        )}
                        Complete
                      </button>
                    )}
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Task Detail Modal with Comments */}
      <AnimatePresence>
        {selectedTask && (
          <TaskDetailModal
            task={selectedTask}
            onClose={() => setSelectedTask(null)}
            onComplete={handleComplete}
            completing={completing}
            setActiveTab={setActiveTab}
            onRefresh={fetchTasks}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// Task Detail Modal with Comments
function TaskDetailModal({ task, onClose, onComplete, completing, setActiveTab, onRefresh }) {
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [sending, setSending] = useState(false);
  const [showComments, setShowComments] = useState(true);
  const commentsEndRef = useRef(null);

  const status = statusConfig[task.status] || statusConfig.pending;
  const StatusIcon = status.icon;
  const isApprovalTask = task.task_type === 'approval_review';

  useEffect(() => {
    fetchComments();
  }, [task.id]);

  const fetchComments = async () => {
    setLoadingComments(true);
    try {
      const res = await fetch(`${API}/api/client/tasks/${task.id}/comments`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setComments(data.comments || []);
      }
    } catch (err) {
      console.error('Error fetching comments:', err);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleSendComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || sending) return;

    setSending(true);
    try {
      const res = await fetch(`${API}/api/client/tasks/${task.id}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: newComment.trim() })
      });

      if (res.ok) {
        setNewComment('');
        fetchComments();
        onRefresh && onRefresh();
        setTimeout(() => commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    } catch (err) {
      console.error('Error sending comment:', err);
    } finally {
      setSending(false);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        onClick={e => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
        data-testid="task-detail-modal"
      >
        {/* Header */}
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${status.bg} ${status.text}`}>
                  <StatusIcon className={`w-3.5 h-3.5 ${task.status === 'in_progress' ? 'animate-spin' : ''}`} />
                  {status.label}
                </span>
                {isApprovalTask && (
                  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                    Approval Review
                  </span>
                )}
              </div>
              <h3 className="text-lg font-bold text-slate-800">{task.title}</h3>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg text-slate-400">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Task Details */}
          <div className="p-6 border-b border-slate-100">
            <p className="text-slate-600 whitespace-pre-wrap">{task.description}</p>
            
            <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-slate-500">
              {task.due_date && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Due: {new Date(task.due_date).toLocaleDateString()}
                </span>
              )}
              {task.sent_by && (
                <span className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  From: {task.sent_by}
                </span>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3 mt-4">
              {isApprovalTask && task.linked_approval_id && task.status !== 'completed' && (
                <button
                  onClick={() => { onClose(); setActiveTab && setActiveTab('approvals'); }}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-xl flex items-center gap-2 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  Go to Approval
                </button>
              )}
              {task.status === 'pending' && !isApprovalTask && (
                <button
                  onClick={() => onComplete(task.id)}
                  disabled={completing === task.id}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-xl flex items-center gap-2 transition-colors disabled:opacity-50"
                >
                  {completing === task.id ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                  Mark Complete
                </button>
              )}
            </div>
          </div>

          {/* Comments Section */}
          <div className="p-6">
            <button
              onClick={() => setShowComments(!showComments)}
              className="flex items-center gap-2 text-slate-700 font-semibold mb-4 hover:text-slate-900"
            >
              <MessageSquare className="w-5 h-5" />
              Comments ({comments.length})
              <ChevronDown className={`w-4 h-4 transition-transform ${showComments ? 'rotate-180' : ''}`} />
            </button>

            {showComments && (
              <>
                {/* Comments List */}
                <div className="space-y-3 max-h-64 overflow-y-auto mb-4">
                  {loadingComments ? (
                    <div className="flex justify-center py-4">
                      <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
                    </div>
                  ) : comments.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">
                      No comments yet. Start the conversation!
                    </p>
                  ) : (
                    comments.map(comment => (
                      <div
                        key={comment.comment_id}
                        className={`p-3 rounded-xl ${
                          comment.author_type === 'client'
                            ? 'bg-blue-50 ml-8'
                            : 'bg-slate-100 mr-8'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs font-medium ${
                            comment.author_type === 'client' ? 'text-blue-700' : 'text-slate-700'
                          }`}>
                            {comment.author_type === 'client' ? 'You' : comment.author_name}
                          </span>
                          <span className="text-xs text-slate-400">
                            {formatDateTime(comment.created_at)}
                          </span>
                        </div>
                        <p className="text-sm text-slate-700">{comment.message}</p>
                      </div>
                    ))
                  )}
                  <div ref={commentsEndRef} />
                </div>

                {/* Comment Input */}
                <form onSubmit={handleSendComment} className="flex gap-2">
                  <input
                    type="text"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment..."
                    className="flex-1 px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    data-testid="task-comment-input"
                  />
                  <button
                    type="submit"
                    disabled={!newComment.trim() || sending}
                    className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-xl flex items-center gap-2 transition-colors"
                    data-testid="send-comment-btn"
                  >
                    {sending ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 bg-slate-50">
          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl font-medium transition-colors"
          >
            Close
          </button>
        </div>
      </motion.div>
    </div>
  );
}
